import React, { useState, useEffect } from "react";
import {
  useTable,
  useSortBy,
  usePagination,
  useGlobalFilter,
  useFilters,
} from "react-table";
import "bootstrap/dist/css/bootstrap.min.css";
import "./ExpandableTable.css";

import { useTranslation } from "react-i18next";
import i18n from "i18next";
import SvgSortdownarrow from "../../../../Components/IconComponents/Sortdownarrow";
import SvgSortuparrow from "../../../../Components/IconComponents/Sortuparrow";
import SvgSorting from "../../../../Components/IconComponents/Sortingarrow";
import { executeSopContactList } from "../../../../Services/EndPoints";
import { getDataById } from "../../../../Services/Services";
import { Button, Modal } from "react-bootstrap";
import { XCircleblack } from "../../../../Components/IconComponents";

/**
 * Default filter component for table columns
 * Provides a simple input field for filtering column data
 */
const DefaultColumnFilter = ({
  column: { filterValue, preFilteredRows, setFilter },
}) => {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => {
        setFilter(e.target.value || undefined);
      }}
      placeholder={`Filter...`}
      style={{
        width: "100%",
        padding: "5px",
        border: "1px solid #ccc",
        borderRadius: "4px",
        marginTop: "5px",
      }}
    />
  );
};

/**
 * ExecuteSopsExpandableTable Component
 * A data table with expandable rows that show contact lists for each SOP task
 * Features: sorting, pagination, filtering, and modal dialogs
 */
const ExecuteSopsExpandableTable = (props) => {
  const {
    columns,
    data,
    numberOfRows,
    headerTableHeadStyle = {},
    tableStyle = {},
    showRowCount = true,
    rowCount,
    setSearchData = () => {},
    enableColumnFilters = false,
    disbalePaginationFilter = false,
    disbalePaginationFilter1 = false,
    disbalePaginationFilter2 = false,
  } = props;

  // State management for expandable rows and contact data
  const [expandedRowIds, setExpandedRowIds] = useState({});
  const [userTaskMap, setUserTaskMap] = useState([]);
  const [expandedRowId, setExpandedRowId] = useState(null);
  
  // State for region details modal
  const [showRegionModal, setShowRegionModal] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState("");

  /**
   * Handle region name click to show full region details in modal
   */
  const handleRegionClick = (region) => {
    setSelectedRegion(region);
    setShowRegionModal(true);
  };

  /**
   * Close region details modal and reset selected region
   */
  const handleRegionModalClose = () => {
    setShowRegionModal(false);
    setSelectedRegion("");
  };

  /**
   * Toggle row expansion state
   * Only allows one row to be expanded at a time
   */
  const toggleRowExpansion = (rowId) => {
    setExpandedRowIds((prev) => {
      const isCurrentlyExpanded = !!prev[rowId];
      if (!isCurrentlyExpanded) {
        setExpandedRowId(rowId);
      } else {
        setExpandedRowId(null);
      }
      return isCurrentlyExpanded ? {} : { [rowId]: true };
    });
  };

  /**
   * Fetch contact list data when a row is expanded
   * Makes API call to get user task mapping based on role ID
   */
  useEffect(() => {
    const fetchContactList = async () => {
      if (expandedRowId) {
        try {
          const expandedRow = rows.find((row) => row.id === expandedRowId);
          if (expandedRow) {
            const roleId = expandedRow.original.role;
            const executeSopContactListResponse = await getDataById(
              executeSopContactList,
              roleId
            );
            setUserTaskMap(executeSopContactListResponse);
          }
        } catch (error) {
          console.error("Error fetching Execute SOP Contact List:", error);
        }
      }
    };

    fetchContactList();
  }, [expandedRowId]);

  // Initialize react-table with hooks for sorting, pagination, and filtering
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    rows,
    state: { pageIndex, globalFilter, pageSize },
  } = useTable(
    {
      columns,
      data,
      defaultColumn: { Filter: DefaultColumnFilter },
      initialState: { pageIndex: 0, pageSize: numberOfRows },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const { t } = useTranslation();

  /**
   * Translate numbers for internationalization support
   */
  const translateNumber = (number) => {
    const numberString = number.toString();
    return i18n.t(`numbers.${numberString}`, { defaultValue: numberString });
  };

  // Calculate pagination display values with fallback defaults
  let tempRowCount = globalFilter ? rows.length : rowCount || data.length;
  let tempPageIndex = pageIndex;
  let tempPageSize = pageSize;

  if (tempPageIndex === undefined || tempPageIndex === null) {
    tempPageIndex = 0;
  }
  if (tempRowCount === undefined || tempRowCount === null) {
    tempRowCount = 0;
  }
  if (tempPageSize === undefined || tempPageSize === null) {
    tempPageSize = 10;
  }

  const startIndex = tempPageIndex * tempPageSize + 1 || 1;
  const endIndex =
    Math.min((tempPageIndex + 1) * tempPageSize, tempRowCount) || tempRowCount;

  const translatedStartIndex = startIndex;
  const translatedEndIndex = endIndex;
  const translatedRowCount = tempRowCount;

  // Update page size when numberOfRows prop changes
  useEffect(() => {
    setPageSize(numberOfRows);
  }, [numberOfRows]);

  // Update search data when global filter or rows change
  useEffect(() => {
    if (globalFilter) {
      const pageData = page?.map((row) => row.original);
      setSearchData(pageData ? pageData : []);
    } else {
      const pageData = rows?.map((row) => row.original);
      setSearchData(pageData ? pageData : []);
    }
  }, [globalFilter, rows]);

  /**
   * Render expanded row content with contact list table
   * Shows detailed contact information for the selected SOP task
   */
  const renderExpandedRow = (row) => {

    return (
      <tr>
        <td colSpan="6">
          <div
            style={{
              width: "85%",
              borderRadius: "10px",
              marginTop: "10px",
              marginLeft: "80px",
              marginBottom: "10px",
            }}
          >
            {/* Contact list table within expanded row */}
            <table
              style={{
                height: "5%",
                borderCollapse: "collapse",
                position: "relative",
              }}
            >
              <thead style={{ position: "sticky"}}>
                <tr style={{ backgroundColor: "#1a7a73" }}>
                  <th
                    style={{
                      padding: "8px",
                      textAlign: "left",
                      borderBottom: "1px solid #ddd",
                      color: "white",
                    }}
                  >
                    {t("User Name")}
                  </th>
                  <th
                    style={{
                      padding: "8px",
                      textAlign: "left",
                      borderBottom: "1px solid #ddd",
                      color: "white",
                    }}
                  >
                    {t("Role Name")}
                  </th>
                  <th
                    style={{
                      padding: "8px",
                      textAlign: "left",
                      borderBottom: "1px solid #ddd",
                      color: "white",
                    }}
                  >
                    {t("Region")}
                  </th>
                  <th
                    style={{
                      padding: "8px",
                      textAlign: "left",
                      borderBottom: "1px solid #ddd",
                      color: "white",
                    }}
                  >
                    {t("Skill")}
                  </th>
                  <th
                    style={{
                      padding: "8px",
                      textAlign: "left",
                      borderBottom: "1px solid #ddd",
                      color: "white",
                    }}
                  >
                    {t("Email")}
                  </th>
                  <th
                    style={{
                      padding: "8px",
                      textAlign: "left",
                      borderBottom: "1px solid #ddd",
                      color: "white",
                    }}
                  >
                    {t("Phone")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {userTaskMap.map((task) => (
                  <tr key={task.id}>
                    <td
                      style={{
                        padding: "8px",
                        textAlign: "left",
                        borderBottom: "1px solid gray",
                      }}
                    >
                      {`${task.first_name} ${task.last_name || ""}`.trim() ||
                        t("NA")}
                    </td>
                    <td
                      style={{
                        padding: "8px",
                        textAlign: "left",
                        borderBottom: "1px solid gray",
                      }}
                    >
                      {task.role_name || t("NA")}
                    </td>
                    {/* Region cell with click handler and text truncation */}
                    <td
                      style={{
                        padding: "8px",
                        textAlign: "left",
                        borderBottom: "1px solid gray",
                        cursor: "pointer",
                        color: "#1a7a73",
                        textDecoration: "underline",
                      }}
                      title={task.region_name || t("NA")}
                      onClick={() =>
                        handleRegionClick(task.region_name || t("NA"))
                      }
                    >
                      {task.region_name?.length > 25
                        ? `${task.region_name.substring(0, 25)}...`
                        : task.region_name || t("NA")}
                    </td>
                    <td
                      style={{
                        padding: "8px",
                        textAlign: "left",
                        borderBottom: "1px solid gray",
                      }}
                    >
                      {task.skill_name || t("NA")}
                    </td>
                    <td
                      style={{
                        padding: "8px",
                        textAlign: "left",
                        borderBottom: "1px solid gray",
                      }}
                    >
                      {task.email || t("NA")}
                    </td>
                    <td
                      style={{
                        padding: "8px",
                        textAlign: "left",
                        borderBottom: "1px solid gray",
                      }}
                    >
                      {task.phone_number || t("NA")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="tabl_container">
      {/* Table header */}
      <div
        style={{
          fontSize: "12pt",
          marginLeft: "27px",
          marginTop: "1rem",
          marginBottom: "1rem",
          color: "#1a7a73",
          fontSize: "17px",
          fontWeight: "700px"
        }}
      >
        Task List
      </div>

      {/* Main table container */}
      <div className="iccc__table--container" style={tableStyle}>
        <table {...getTableProps()} className="table iccc--table">
          <thead style={{backgroundColor: "#1a7a73"}}>
            <tr>
              {/* Expand/collapse column */}
              <th
                style={{
                  width: "30px",
                  textAlign: "left",
                  color: "white",
                  backgroundColor: "#1a7a73",
                }}
              >
                {t("")}
              </th>
              {/* Dynamic table headers with sorting and filtering */}
              {headerGroups.map((headerGroup, index) => (
                <React.Fragment key={index}>
                  {headerGroup.headers.map((column, index) => (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      style={headerTableHeadStyle}
                      key={index}
                    >
                      {column.render("Header")}
                      {/* Sort indicators */}
                      {!column.disableSortBy &&
                        (column.isSorted ? (
                          column.isSortedDesc ? (
                            <SvgSortdownarrow />
                          ) : (
                            <SvgSortuparrow />
                          )
                        ) : (
                          <SvgSorting />
                        ))}
                      {/* Column filters */}
                      {enableColumnFilters && column.canFilter && (
                        <div>{column.render("Filter")}</div>
                      )}
                    </th>
                  ))}
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.length > 0 ? (
              page.map((row, i) => {
                prepareRow(row);
                const isExpanded = expandedRowIds[row.id];

                return (
                  <React.Fragment key={row.id}>
                    {/* Main table row */}
                    <tr {...row.getRowProps()}>
                      {/* Expand/collapse button */}
                      <td style={{ textAlign: "left" }}>
                        <span
                          onClick={() => toggleRowExpansion(row.id)}
                          style={{ cursor: "pointer", fontSize: "18px" }}
                        >
                          {isExpanded ? "-" : "+"}
                        </span>
                      </td>
                      {/* Table cells */}
                      {row.cells.map((cell) => (
                        <td
                          {...cell.getCellProps()}
                          key={cell.column.id}
                          style={{ textAlign: "left" }}
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                    {/* Expanded row content */}
                    {isExpanded && renderExpandedRow(row)}
                  </React.Fragment>
                );
              })
            ) : (
              // No data fallback
              <tr>
                <td colSpan={columns.length + 1} className="no-data-text">
                  No data Found
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Region details modal */}
        <Modal
          show={showRegionModal}
          onHide={handleRegionModalClose}
          dialogClassName="custom-modal"
          centered
          style={{
            borderRadius: "20px",
            border: "none",
            top: "20%",
          }}
        >
          <Modal.Header
            style={{
              backgroundColor: "#1a7a73",
              color: "white",
              borderBottom: "none",
            }}
          >
            <Modal.Title style={{ color: "white", marginRight:"250px" }}>
              Region Details
            </Modal.Title>
            <XCircleblack
              onClick={handleRegionModalClose}
              width="1.5em"
              height="1.5em"
              className="cursor-pointer"
            />
          </Modal.Header>
          <Modal.Body
            style={{
              backgroundColor: "#fcfcfc",
              color: "#001b30",
              border: "none",
              padding: "20px",
              borderRadius: "0px",
              lineHeight: "1.8",
              wordBreak: "break-word",
              whiteSpace: "pre-wrap",
              maxHeight: "400px",
              overflowY: "auto",
            }}
          >
            <p>{selectedRegion}</p>
          </Modal.Body>
          <Modal.Footer
            style={{
              backgroundColor: "#fcfcfc",
              color: "white",
              borderTop: "none",
            }}
          >
            <Button
              variant="secondary"
              onClick={handleRegionModalClose}
              style={{
                backgroundColor: "#1a7a73",
                borderColor: "#003366",
                borderRadius: "5px",
              }}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>

      {/* Pagination controls */}
      <div className="d-flex justify-content-between align-items-center">
        {/* Row count display */}
        {!disbalePaginationFilter2 && (
          <div className="float-start m-2 mt-2 ms-3">
            {showRowCount && (
              <div className="float-start m-2 mt-1 ms-3 pagecount-style">
                {t("showingentriestable.showing")} {translatedStartIndex}{" "}
                {t("showingentriestable.to")} {translatedEndIndex}{" "}
                {t("showingentriestable.of")} {translatedRowCount}{" "}
                {t("showingentriestable.entries")}
              </div>
            )}
          </div>
        )}

        {/* Pagination controls */}
        <div className="pagination">
          {/* Page navigation and page size selector */}
          {!disbalePaginationFilter && (
            <div className="d-flex">
              <span className="pagination_gotopage">
                Go to Page:
                <input
                  type="number"
                  defaultValue={pageIndex + 1}
                  onChange={(e) => {
                    const pageNumber = e.target.value
                      ? Number(e.target.value) - 1
                      : 0;
                    gotoPage(pageNumber);
                  }}
                  className="pagination_gotopage-input"
                />
              </span>
              <span>
                <select
                  className="pagination_select"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  {[10, 50, 100, 500, 1000, 2000].map((pageSize) => (
                    <option key={pageSize} value={pageSize}>
                      show {pageSize}
                    </option>
                  ))}
                </select>
              </span>
            </div>
          )}
          
          {/* Previous/Next page buttons */}
          {!disbalePaginationFilter1 && (
            <>
              <button
                className="pagebtn mx-2"
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
              >
                {"<"}
              </button>
              <button className="pagination_page">
                {translateNumber(pageIndex + 1)}{" "}
              </button>
              <button
                className="pagebtn"
                onClick={() => nextPage()}
                disabled={!canNextPage}
              >
                {">"}
              </button>{" "}
              <span className="right_pageno">
                {t("pagination.page_of", {
                  page: translateNumber(pageIndex + 1),
                  total: translateNumber(pageOptions.length),
                })}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExecuteSopsExpandableTable;